import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  BugReport as BugIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  Terminal as TerminalIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useAuth } from 'context/AuthContext';
import {
  createExploit,
  createExploitTemplate,
  linkExploitFinding,
  listExploits,
  recordExploitAttempt
} from 'api/features';

const BRAND = { primary: '#22c55e', primaryDark: '#15803d', critical: '#ef4444', warning: '#f59e0b', info: '#0ea5e9' };
const PanelCard = styled(Card)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(BRAND.primary, 0.34)}`,
  boxShadow: 'none',
  background: theme.palette.background.paper
}));

const exploitTypes = ['rce', 'auth_bypass', 'sqli', 'xss', 'dos', 'privilege_escalation', 'other'];
const severities = ['critical', 'high', 'medium', 'low', 'info'];
const languages = ['http', 'python', 'bash', 'nuclei', 'other'];
const outcomes = ['success', 'failed', 'blocked', 'not_tested'];

const initialExploit = {
  title: '',
  cve_id: '',
  exploit_type: 'other',
  severity: 'high',
  platform: '',
  verified: false,
  source_url: '',
  description: '',
  poc_code: ''
};
const initialTemplate = { name: '', language: 'http', template_code: '', safety_notes: '' };
const initialLink = { finding: '', status: 'relevant', notes: '' };
const initialAttempt = { finding: '', outcome: 'not_tested', target: '', evidence: '', notes: '' };

const sevStyle = (s) => ({
  critical: { bgcolor: 'rgba(239,68,68,0.14)', color: '#ef4444' },
  high: { bgcolor: 'rgba(249,115,22,0.14)', color: '#f97316' },
  medium: { bgcolor: 'rgba(245,158,11,0.14)', color: '#f59e0b' },
  low: { bgcolor: 'rgba(34,197,94,0.14)', color: '#22c55e' },
  info: { bgcolor: 'rgba(100,116,139,0.14)', color: '#64748b' }
}[s] || { bgcolor: 'rgba(107,114,128,0.12)', color: '#6b7280' });
const outcomeColor = (outcome) => ({ success: '#22c55e', failed: '#ef4444', blocked: '#f59e0b', not_tested: '#64748b' }[outcome] || '#64748b');

const ExploitDbPage = () => {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all');
  const [type, setType] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [exploits, setExploits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [exploitDialog, setExploitDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [linkDialog, setLinkDialog] = useState(false);
  const [attemptDialog, setAttemptDialog] = useState(false);
  const [exploitForm, setExploitForm] = useState(initialExploit);
  const [templateForm, setTemplateForm] = useState(initialTemplate);
  const [linkForm, setLinkForm] = useState(initialLink);
  const [attemptForm, setAttemptForm] = useState(initialAttempt);

  const loadExploits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (severity !== 'all') params.severity = severity;
      if (type !== 'all') params.exploit_type = type;
      const resp = await listExploits(params);
      setExploits(Array.isArray(resp) ? resp : resp.results || []);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [search, severity, type]);

  useEffect(() => { loadExploits(); }, [loadExploits]);

  const stats = useMemo(() => ({
    total: exploits.length,
    critical: exploits.filter((e) => e.severity === 'critical').length,
    templates: exploits.reduce((sum, e) => sum + (e.templates_count || e.templates?.length || 0), 0),
    successful: exploits.reduce((sum, e) => sum + (e.success_count || 0), 0),
    linked: exploits.reduce((sum, e) => sum + (e.linked_findings_count || e.finding_links?.length || 0), 0)
  }), [exploits]);

  const openTemplate = (exploit) => { setSelected(exploit); setTemplateForm(initialTemplate); setTemplateDialog(true); };
  const openLink = (exploit) => { setSelected(exploit); setLinkForm(initialLink); setLinkDialog(true); };
  const openAttempt = (exploit) => { setSelected(exploit); setAttemptForm(initialAttempt); setAttemptDialog(true); };

  const handleCreateExploit = async () => {
    try {
      await createExploit({ ...exploitForm, verified: Boolean(exploitForm.verified) });
      setExploitDialog(false);
      setExploitForm(initialExploit);
      setSnackbar({ open: true, severity: 'success', message: 'Exploit entry created.' });
      await loadExploits();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await createExploitTemplate(selected.id, { ...templateForm, variables: {} });
      setTemplateDialog(false);
      setSnackbar({ open: true, severity: 'success', message: 'Exploit template saved.' });
      await loadExploits();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleLinkFinding = async () => {
    try {
      await linkExploitFinding(selected.id, linkForm);
      setLinkDialog(false);
      setSnackbar({ open: true, severity: 'success', message: 'Finding linked to exploit.' });
      await loadExploits();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleRecordAttempt = async () => {
    try {
      await recordExploitAttempt(selected.id, attemptForm);
      setAttemptDialog(false);
      setSnackbar({ open: true, severity: 'success', message: 'Exploit outcome recorded.' });
      await loadExploits();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  return (
    <Box className="fuzzscan-page scanner-pro-page">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Exploit library</Typography>
          <Typography color="text.secondary">
            {stats.total} entries, {stats.critical} critical, {stats.templates} templates, {stats.successful} successful validations. Proofs of concept are kept as references and templates; running them is recorded by hand.
          </Typography>
        </Box>
        {isAdmin && <Button variant="contained" onClick={() => setExploitDialog(true)} sx={{ height: 44, fontWeight: 700, bgcolor: BRAND.primary, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>New exploit</Button>}
      </Stack>

      <PanelCard sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={{ xs: 12, lg: 6 }}>
              <TextField fullWidth size="small" placeholder="Search CVE, title, platform, description..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.8, color: 'text.secondary' }} /> }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <MenuItem value="all">All severities</MenuItem>
                {severities.map((s) => <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value="all">All types</MenuItem>
                {exploitTypes.map((t) => <MenuItem key={t} value={t}>{t.replaceAll('_', ' ').toUpperCase()}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </PanelCard>

      <PanelCard>
        <CardHeader title="Exploit Library" titleTypographyProps={{ variant: 'h6', fontWeight: 800 }} subheader="PoCs are stored as references/templates only; execution is tracked manually for auditability." />
        {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: BRAND.primary } }} />}
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>CVE</TableCell><TableCell>Title</TableCell><TableCell>Severity</TableCell><TableCell>Type</TableCell><TableCell>Platform</TableCell><TableCell>Links</TableCell><TableCell>Outcomes</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {exploits.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No exploits found.</Typography></TableCell></TableRow>
              )}
              {exploits.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={800} fontFamily="monospace" color="primary">{e.cve_id || '—'}</Typography></TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography variant="body2" fontWeight={800}>{e.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{e.verified ? 'Verified reference' : 'Unverified'} · {e.source_url || 'internal'}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={(e.severity || 'high').toUpperCase()} sx={{ ...sevStyle(e.severity), fontWeight: 800, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={(e.exploit_type || 'other').replaceAll('_', ' ')} sx={{ fontSize: '0.7rem' }} /></TableCell>
                  <TableCell><Typography variant="caption">{e.platform || '—'}</Typography></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Chip size="small" label={`${e.linked_findings_count || 0} findings`} sx={{ bgcolor: alpha(BRAND.primary, 0.11), color: BRAND.primaryDark, fontWeight: 800 }} />
                      <Chip size="small" label={`${e.templates_count || e.templates?.length || 0} templates`} sx={{ bgcolor: 'rgba(14,165,233,0.11)', color: '#0284c7', fontWeight: 800 }} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Chip size="small" label={`${e.success_count || 0} ok`} sx={{ bgcolor: alpha('#22c55e', 0.12), color: '#16a34a', fontWeight: 800 }} />
                      <Chip size="small" label={`${e.failure_count || 0} fail`} sx={{ bgcolor: alpha('#ef4444', 0.12), color: '#ef4444', fontWeight: 800 }} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View details"><IconButton size="small" onClick={() => setSelected(e)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Link finding"><IconButton size="small" onClick={() => openLink(e)}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Add template"><IconButton size="small" onClick={() => openTemplate(e)}><CodeIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Record outcome"><IconButton size="small" onClick={() => openAttempt(e)}><PlayIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PanelCard>

      <Dialog open={Boolean(selected) && !templateDialog && !linkDialog && !attemptDialog} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{selected?.title}</DialogTitle>
        <DialogContent>
          {selected && <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={selected.cve_id || 'No CVE'} />
              <Chip label={selected.exploit_type?.replaceAll('_', ' ')} />
              <Chip label={selected.severity?.toUpperCase()} sx={sevStyle(selected.severity)} />
              {selected.verified && <Chip label="Verified" color="success" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">{selected.description || 'No description provided.'}</Typography>
            {selected.poc_code && <TextField fullWidth multiline minRows={6} label="PoC Code" value={selected.poc_code} InputProps={{ readOnly: true }} />}
            <Divider />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" fontWeight={800}>Linked Findings</Typography>
                {(selected.finding_links || []).slice(0, 6).map((l) => <Typography key={l.id} variant="body2">{l.finding_severity?.toUpperCase()} · {l.finding_title}</Typography>)}
                {(selected.finding_links || []).length === 0 && <Typography variant="body2" color="text.secondary">No linked findings.</Typography>}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" fontWeight={800}>Templates</Typography>
                {(selected.templates || []).slice(0, 6).map((t) => <Typography key={t.id} variant="body2">{t.language} · {t.name}</Typography>)}
                {(selected.templates || []).length === 0 && <Typography variant="body2" color="text.secondary">No templates.</Typography>}
              </Grid>
            </Grid>
          </Stack>}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={exploitDialog} onClose={() => setExploitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Create Exploit Reference</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth size="small" label="Title" value={exploitForm.title} onChange={(ev) => setExploitForm((p) => ({ ...p, title: ev.target.value }))} />
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 4 }}><TextField fullWidth size="small" label="CVE" value={exploitForm.cve_id} onChange={(ev) => setExploitForm((p) => ({ ...p, cve_id: ev.target.value.toUpperCase() }))} /></Grid>
            <Grid size={{ xs: 12, md: 4 }}><TextField select fullWidth size="small" label="Type" value={exploitForm.exploit_type} onChange={(ev) => setExploitForm((p) => ({ ...p, exploit_type: ev.target.value }))}>{exploitTypes.map((t) => <MenuItem key={t} value={t}>{t.replaceAll('_', ' ')}</MenuItem>)}</TextField></Grid>
            <Grid size={{ xs: 12, md: 4 }}><TextField select fullWidth size="small" label="Severity" value={exploitForm.severity} onChange={(ev) => setExploitForm((p) => ({ ...p, severity: ev.target.value }))}>{severities.map((s) => <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>)}</TextField></Grid>
          </Grid>
          <TextField fullWidth size="small" label="Platform/Product" value={exploitForm.platform} onChange={(ev) => setExploitForm((p) => ({ ...p, platform: ev.target.value }))} />
          <TextField fullWidth size="small" label="Source URL" value={exploitForm.source_url} onChange={(ev) => setExploitForm((p) => ({ ...p, source_url: ev.target.value }))} />
          <TextField fullWidth multiline minRows={3} label="Description" value={exploitForm.description} onChange={(ev) => setExploitForm((p) => ({ ...p, description: ev.target.value }))} />
          <TextField fullWidth multiline minRows={5} label="PoC Code / Notes" value={exploitForm.poc_code} onChange={(ev) => setExploitForm((p) => ({ ...p, poc_code: ev.target.value }))} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setExploitDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleCreateExploit}>Create</Button></DialogActions>
      </Dialog>

      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Add PoC Template</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth size="small" label="Template name" value={templateForm.name} onChange={(ev) => setTemplateForm((p) => ({ ...p, name: ev.target.value }))} />
          <TextField select fullWidth size="small" label="Language" value={templateForm.language} onChange={(ev) => setTemplateForm((p) => ({ ...p, language: ev.target.value }))}>{languages.map((l) => <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>)}</TextField>
          <TextField fullWidth multiline minRows={8} label="Template code" value={templateForm.template_code} onChange={(ev) => setTemplateForm((p) => ({ ...p, template_code: ev.target.value }))} />
          <TextField fullWidth multiline minRows={2} label="Safety notes" value={templateForm.safety_notes} onChange={(ev) => setTemplateForm((p) => ({ ...p, safety_notes: ev.target.value }))} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setTemplateDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleCreateTemplate}>Save Template</Button></DialogActions>
      </Dialog>

      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Link Finding to Exploit</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth size="small" label="Finding UUID" value={linkForm.finding} onChange={(ev) => setLinkForm((p) => ({ ...p, finding: ev.target.value }))} />
          <TextField select fullWidth size="small" label="Link status" value={linkForm.status} onChange={(ev) => setLinkForm((p) => ({ ...p, status: ev.target.value }))}>{['relevant', 'needs_review', 'false_match'].map((s) => <MenuItem key={s} value={s}>{s.replaceAll('_', ' ')}</MenuItem>)}</TextField>
          <TextField fullWidth multiline minRows={3} label="Notes" value={linkForm.notes} onChange={(ev) => setLinkForm((p) => ({ ...p, notes: ev.target.value }))} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setLinkDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleLinkFinding}>Link</Button></DialogActions>
      </Dialog>

      <Dialog open={attemptDialog} onClose={() => setAttemptDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Record Exploit Outcome</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth size="small" label="Finding UUID (optional)" value={attemptForm.finding} onChange={(ev) => setAttemptForm((p) => ({ ...p, finding: ev.target.value }))} />
          <TextField select fullWidth size="small" label="Outcome" value={attemptForm.outcome} onChange={(ev) => setAttemptForm((p) => ({ ...p, outcome: ev.target.value }))}>{outcomes.map((o) => <MenuItem key={o} value={o}>{o.replaceAll('_', ' ')}</MenuItem>)}</TextField>
          <TextField fullWidth size="small" label="Target" value={attemptForm.target} onChange={(ev) => setAttemptForm((p) => ({ ...p, target: ev.target.value }))} />
          <TextField fullWidth multiline minRows={3} label="Evidence" value={attemptForm.evidence} onChange={(ev) => setAttemptForm((p) => ({ ...p, evidence: ev.target.value }))} />
          <TextField fullWidth multiline minRows={2} label="Notes" value={attemptForm.notes} onChange={(ev) => setAttemptForm((p) => ({ ...p, notes: ev.target.value }))} />
          <Chip size="small" label={`Outcome color: ${attemptForm.outcome}`} sx={{ width: 'fit-content', bgcolor: alpha(outcomeColor(attemptForm.outcome), 0.12), color: outcomeColor(attemptForm.outcome), fontWeight: 800 }} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setAttemptDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleRecordAttempt}>Record</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExploitDbPage;
